<template>
  <Dialog
    v-model:visible="isVisible"
    :header="title"
    :modal="true"
    :closable="true"
    :draggable="false"
    class="w-11/12 md:w-96"
    @update:visible="handleClose">

    <div class="flex flex-col gap-4 p-4">
      <p class="text-sm text-gray-700">{{ message }}</p>

      <div class="flex flex-col gap-3">
        <Button
          :label="thisInstanceLabel"
          icon="pi pi-calendar"
          class="w-full"
          outlined
          @click="handleThisInstance"/>

        <Button
          :label="seriesLabel"
          icon="pi pi-calendar-plus"
          class="w-full"
          outlined
          @click="handleSeries"/>
      </div>
    </div>
  </Dialog>
</template>

<script lang="ts" setup>
import {ref, computed, watch} from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { trackEvent } from '@app/composables/posthog';

export type RecurringActionType = 'edit' | 'delete';
export type RecurringActionScope = 'instance' | 'series';

const props = defineProps<{
  visible: boolean;
  actionType: RecurringActionType;
  eventTitle?: string;
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'action', scope: RecurringActionScope): void;
}>();

const isVisible = ref(props.visible);

watch(() => props.visible, (newVal) => {
  isVisible.value = newVal;
});

const title = computed(() => {
  return props.actionType === 'edit' ? 'עריכת אירוע חוזר' : 'מחיקת אירוע חוזר';
});

const message = computed(() => {
  const eventName = props.eventTitle ? `"${props.eventTitle}"` : 'אירוע זה';
  if (props.actionType === 'edit') {
    return `${eventName} הוא חלק מסדרת אירועים חוזרים. האם לערוך רק את המופע הזה או את כל הסדרה?`;
  } else {
    return `${eventName} הוא חלק מסדרת אירועים חוזרים. האם למחוק רק את המופע הזה או את כל הסדרה העתידית?`;
  }
});

const thisInstanceLabel = computed(() => {
  return props.actionType === 'edit' ? 'עריכת מופע זה בלבד' : 'מחיקת מופע זה בלבד';
});

const seriesLabel = computed(() => {
  return props.actionType === 'edit' ? 'עריכת כל הסדרה העתידית' : 'מחיקת כל הסדרה העתידית';
});

function handleThisInstance() {
  trackEvent('recurring_event_action_this_instance');
  emit('action', 'instance');
  handleClose();
}

function handleSeries() {
  trackEvent('recurring_event_action_series');
  emit('action', 'series');
  handleClose();
}

function handleClose() {
  emit('update:visible', false);
}
</script>
